import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Code2, ShieldCheck, Terminal, Workflow } from "lucide-react";
import { api, type SchemaFunction } from "@/lib/api";
import { ROUTES } from "@/routes";
import { EmptyState, ErrorBox, PageHeader, Spinner } from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export function DatabaseFunctionsPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [functions, setFunctions] = useState<SchemaFunction[]>([]);
  const [schemaName, setSchemaName] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    void api
      .getSchema(id)
      .then(({ data }) => {
        if (cancelled) return;
        setFunctions(data.functions ?? []);
        setSchemaName(data.schema_name ?? "");
        setError(null);
      })
      .catch((nextError) => {
        if (!cancelled) setError(nextError);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) return <Spinner label="Loading database functions…" />;

  return (
    <div>
      <PageHeader
        eyebrow="Database"
        title="Database Functions"
        description="Inspect PostgreSQL functions and procedures defined in the project schema."
        actions={
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">{functions.length} functions</Badge>
            {schemaName ? <Badge variant="outline">{schemaName}</Badge> : null}
          </div>
        }
      />
      {error ? (
        <div className="mb-4">
          <ErrorBox error={error} />
        </div>
      ) : null}
      {functions.length === 0 ? (
        <EmptyState title="No database functions yet" icon={<Workflow className="h-5 w-5" />}>
          Create a function in the SQL editor to see it here.
        </EmptyState>
      ) : (
        <div className="grid gap-3 lg:grid-cols-2" data-testid="database-functions">
          {functions.map((fn) => (
            <Card key={`${fn.name}-${fn.arguments}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="flex items-center gap-2 font-sans text-base">
                      <Code2 className="h-4 w-4 shrink-0 text-secondary" aria-hidden />
                      <span className="truncate font-mono">{fn.name}</span>
                    </CardTitle>
                    <CardDescription className="mt-1 font-mono text-xs">
                      {fn.kind || "function"}({fn.arguments || ""})
                    </CardDescription>
                  </div>
                  {fn.security_definer ? (
                    <Badge variant="warning" className="shrink-0">
                      <ShieldCheck className="mr-1 h-3 w-3" aria-hidden />
                      Security definer
                    </Badge>
                  ) : null}
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid gap-2 text-xs sm:grid-cols-2">
                  <div className="rounded-sm bg-background-subtle px-3 py-2">
                    <span className="block text-text-muted">Returns</span>
                    <span className="font-mono text-text-primary">{fn.returns || "unknown"}</span>
                  </div>
                  <div className="rounded-sm bg-background-subtle px-3 py-2">
                    <span className="block text-text-muted">Language</span>
                    <span className="font-mono text-text-primary">{fn.language || "unknown"}</span>
                  </div>
                </div>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`${ROUTES.sql(id)}?routine=${encodeURIComponent(fn.name)}`}>
                    <Terminal className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                    Open SQL editor
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
